import { useState } from 'react'
import { Bell, TriangleAlert, UserPlus } from 'lucide-react'

import { ApiError } from '@/lib/api'
import { type MyNotification } from '@/modules/notifications/api/notifications-api'
import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { LoadingNotice } from '@/ui/loading-notice'
import { buttonVariants } from '@/ui/button-variants'
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/ui/popover'
import { RelativeTime } from '@/ui/relative-time'
import { useAssistantBrowserCommand } from '@/lib/assistant-browser-commands'
import { assistantBrowserCommands } from '@/lib/assistant-browser-commands.generated'

import {
  useMarkAllNotificationsRead,
  useMyNotifications,
  useUnreadNotificationCount,
} from './notification-bell-queries'

function errorMessage(error: unknown): string {
  const generic = "Couldn't load notifications. Try again."
  if (error instanceof ApiError && error.status < 500) {
    return error.problem?.detail ?? error.problem?.title ?? generic
  }
  return generic
}

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const query = useMyNotifications()
  const markAllRead = useMarkAllNotificationsRead()
  const notifications = query.data?.items ?? []
  // Server-side count: the fetched page is capped, so counting items undercounts.
  const unreadCount = useUnreadNotificationCount().data?.unreadCount ?? 0

  useAssistantBrowserCommand(assistantBrowserCommands.notifications, ({ open: desired }) => {
    setOpen(desired)
    return true
  })

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          buttonVariants({ variant: 'chrome', size: 'icon-lg' }),
          'relative',
        )}
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : 'Notifications, no unread'
        }
      >
        <Bell className="size-4" />
        {unreadCount > 0 && (
          <span
            aria-hidden
            className="bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full px-1 text-[9px] leading-4 font-medium"
          >
            {unreadCount}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-80 gap-0 p-0">
        <PopoverHeader className="border-border flex flex-row items-center justify-between border-b px-3 py-2">
          <PopoverTitle>Notifications</PopoverTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={unreadCount === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark all read
          </Button>
        </PopoverHeader>

        {query.isLoading ? (
          <LoadingNotice message="Loading notifications…" />
        ) : query.error ? (
          <p role="alert" className="text-destructive p-4 text-sm">
            {errorMessage(query.error)}
          </p>
        ) : notifications.length === 0 ? (
          <p role="status" className="text-muted-foreground p-4 text-sm">
            You have no notifications.
          </p>
        ) : (
          <ul
            aria-live="polite"
            className="flex max-h-96 flex-col overflow-y-auto"
          >
            {notifications.map((notification) => (
              <NotificationRow
                key={notification.notificationId}
                notification={notification}
              />
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  )
}

function CategoryIcon({ category }: { category: string }) {
  const resource = category.split('.')[0]
  const Icon =
    resource === 'invitation'
      ? UserPlus
      : resource === 'alert' || resource === 'event_rule'
        ? TriangleAlert
        : Bell
  return <Icon className="text-muted-foreground mt-0.5 size-3.5 shrink-0" aria-hidden />
}

function NotificationRow({ notification }: { notification: MyNotification }) {
  const resource = notification.category.split('.')[0]
  return (
    <li className="border-border flex items-start gap-2.5 border-b px-3 py-2.5 last:border-b-0">
      <CategoryIcon category={notification.category} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-foreground truncate text-sm font-medium">
          {notification.subject ?? '(no subject)'}
        </span>
        <div className="text-muted-foreground flex items-center gap-2 text-[11px]">
          <span className={resource === 'event_rule' ? undefined : 'capitalize'}>
            {resource === 'event_rule' ? 'Event rule' : resource}
          </span>
          <span aria-hidden>·</span>
          <RelativeTime iso={notification.createdAt} />
        </div>
      </div>
      {!notification.isRead && (
        <span className="mt-1.5 shrink-0">
          <span className="bg-primary block size-1.5 rounded-full" aria-hidden />
          <span className="sr-only">Unread</span>
        </span>
      )}
    </li>
  )
}
